import { PER_PAGE_OPTIONS } from '@/consts/pagination';
import { iconContainerStyles, pageButtonStyles } from './styled';
import { getVisiblePages } from './utils';
import ChevronDownIcon from '/public/icons/chevron-down.svg';
import ChevronLeftIcon from '/public/icons/chevron-left-pagination.svg';
import ChevronRightIcon from '/public/icons/chevron-right-pagination.svg';
import DoubleChevronLeftIcon from '/public/icons/double-chevron-left.svg';
import DoubleChevronRightIcon from '/public/icons/double-chevron-right.svg';

interface Props {
  currentPage: number;
  totalItems: number;
  totalPages: number;
  itemsPerPage: number;
  onPageChange: (value: number) => void;
  onPerPageChange?: (value: number) => void;
}

export function PaginationBar({
  currentPage,
  totalItems,
  totalPages,
  itemsPerPage,
  onPageChange,
  onPerPageChange,
}: Props) {
  const pages = getVisiblePages({ currentPage, totalPages });

  const handlePageChange = (value: number | string) => {
    if (typeof value === 'string' || currentPage === value) return;

    onPageChange(value);
  };

  const handlePrevPage = () => {
    if (currentPage === 1 || totalPages === 0) return;
    onPageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage === totalPages || totalPages === 0) return;
    onPageChange(currentPage + 1);
  };

  const handleFirstPage = () => {
    if (currentPage === 1 || totalPages === 0) return;
    onPageChange(1);
  };

  const handleLastPage = () => {
    if (currentPage === totalPages || totalPages === 0) return;
    onPageChange(totalPages);
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <nav className="flex w-full flex-col items-center gap-5 py-1.5 max-md:py-0 md:flex-row md:justify-between md:gap-2">
      <div className="flex items-center gap-2 text-body-small text-grey-700 max-md:leading-normal md:text-body-regular">
        {onPerPageChange ? (
          <span>
            {totalItems > 0 ? startItem : 0}–{totalItems > 0 ? endItem : 0} of {totalItems}
          </span>
        ) : (
          <span>
            Showing {totalItems > 0 ? startItem : 0} to {totalItems > 0 ? endItem : 0} of {totalItems} entries
          </span>
        )}
        {onPerPageChange && (
          <>
            <span>|</span>
            <div className="flex items-center gap-1.5">
              <span>Items per page</span>
              <label className="flex items-center max-md:h-8 max-md:gap-0.5 max-md:rounded-md max-md:border max-md:border-grey-400 max-md:bg-white max-md:px-2 max-md:py-0.5 max-md:shadow-xs">
                <select
                  value={itemsPerPage}
                  onChange={(event) => onPerPageChange(Number(event.target.value))}
                  className="h-8 cursor-pointer rounded-md border border-grey-400 bg-white px-2 py-0.5 text-sm text-black shadow-xs max-md:h-auto max-md:min-w-4 max-md:appearance-none max-md:rounded-none max-md:border-0 max-md:bg-transparent max-md:p-0 max-md:text-center max-md:shadow-none"
                >
                  {PER_PAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon
                  className="pointer-events-none hidden h-5 w-5 shrink-0 p-1.25 text-black max-md:block"
                  aria-hidden
                />
              </label>
            </div>
          </>
        )}
      </div>
      <div className="flex items-center gap-2 max-md:justify-center max-md:gap-1.5">
        <div
          className={iconContainerStyles({ disabled: currentPage === 1, className: 'max-md:-mr-1' })}
          onClick={handleFirstPage}
        >
          <DoubleChevronLeftIcon className="w-[20px] h-[20px]" />
        </div>
        <div className={iconContainerStyles({ disabled: currentPage === 1 })} onClick={handlePrevPage}>
          <ChevronLeftIcon className="w-[14px] h-[14px]" />
        </div>
        {pages.map((page, index) => (
          <div
            key={`${page}-${index}`}
            className={pageButtonStyles({
              selected: currentPage === page,
              disabled: typeof page === 'string' || currentPage === page,
              dots: typeof page === 'string',
            })}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </div>
        ))}
        <div
          className={iconContainerStyles({ disabled: currentPage === totalPages || totalPages === 0 })}
          onClick={handleNextPage}
        >
          <ChevronRightIcon className="w-[20px] h-[20px]" />
        </div>
        <div
          className={iconContainerStyles({
            disabled: currentPage === totalPages || totalPages === 0,
            className: 'max-md:-ml-1',
          })}
          onClick={handleLastPage}
        >
          <DoubleChevronRightIcon className="w-[20px] h-[20px]" />
        </div>
      </div>
    </nav>
  );
}
